import { useQuery } from '@apollo/client';
import { GET_ME, Me } from '@frontend/data-access';
import { PageTitle } from '@frontend/shared/ui';
import { capFirst } from '@frontend/shared/util';
import { Segmented, Space, Spin } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useState } from 'react';

import CompaniesGraphs from '../CompaniesGraphs';
import IdeasGraphs from '../IdeasGraphs';
import InnovationSpaceGraphs from '../InnovationSpaceGraphs';
import InsightsGraphs from '../InsightsGraphs';
import IntraChallengesGraphs from '../IntraChallengesGraphs';
import OpenChallengesGraphs from '../OpenChallengesGraphs';
import TrendGraphs from '../TrendGraphs';
import UsersGraphs from '../UsersGraphs';

type TagType =
  | 'insights'
  | 'users'
  | 'companies'
  | 'open-challenges'
  | 'spark-innovation'
  | 'ideas'
  | 'trend'
  | 'innovation-space';

const DashboardGraphs: React.FC = () => {
  const { t } = useTranslation('admin');
  const router = useRouter();
  const { graph } = router.query;
  const [type, setType] = useState<TagType>('insights');

  const { data, loading } = useQuery<{ me: Me }>(GET_ME);

  const handleChangeType = (type: TagType) => {
    router.push({
      query: {
        ...router.query,
        graph: type,
      },
    });
  };

  const tagOptions = useMemo(
    () => [
      { value: 'insights', label: t('insights') },
      { value: 'users', label: t('users') },
      { value: 'companies', label: t('companies') },
      { value: 'open-challenges', label: t('open_challenges') },
      { value: 'spark-innovation', label: t('spark_innovation') },
      { value: 'ideas', label: t('ideas') },
      { value: 'innovation-space', label: t('innovation_space') },
      { value: 'trend', label: t('trend') },
    ],
    [t]
  );

  useEffect(() => {
    const graphType = graph as string;
    if (tagOptions.some((option) => option.value === graphType)) {
      setType(graphType as TagType);
    } else {
      setType('insights');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  const renderPage = () => {
    switch (type) {
      case 'insights':
        return <InsightsGraphs />;
      case 'users':
        return <UsersGraphs />;
      case 'open-challenges':
        return <OpenChallengesGraphs />;
      case 'spark-innovation':
        return <IntraChallengesGraphs />;
      case 'ideas':
        return <IdeasGraphs />;
      case 'companies':
        return <CompaniesGraphs />;
      case 'trend':
        return <TrendGraphs />;
      case 'innovation-space':
        return <InnovationSpaceGraphs />;
      default:
        return <UsersGraphs />;
    }
  };

  if (loading) {
    return (
      <div className="text-center p-6">
        <Spin />
      </div>
    );
  }

  return (
    <Space className="w-full" direction="vertical" size="small">
      <PageTitle
        title={t('dashboard')}
        description={`${t('welcome')}${
          data?.me?.fullName ? ', ' + capFirst(data?.me?.fullName) + '!' : ''
        }`}
      />
      <div className="mb-4 overflow-auto">
        <Segmented
          rootClassName="overflow-x-auto"
          options={tagOptions}
          onChange={handleChangeType}
          value={type}
        />
      </div>
      {data?.me.role === 'ADMIN' ? (
        renderPage()
      ) : (
        <div>
          {t('go_to')}{' '}
          <Link href="/intrapreneurship/challenge" className="text-primary-600">
            {t('intrapreneurship_challenge')}
          </Link>{' '}
          {t('to_create_and_manage_new_challenges')}
        </div>
      )}
    </Space>
  );
};
export default DashboardGraphs;
